import { Component } from '@theme/component';

/**
 * Crossfade photo slideshow for the home hero.
 *
 * The welcome copy stays outside this element. Photos advance on a timer,
 * and visitors can move them with the arrows, the dots, or the keyboard.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [pause] - Pause and resume control.
 *
 * @extends {Component<Refs>}
 */
class PetHeroSlideshow extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number} */
  #timer = 0;

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #userPaused = false;

  /** @type {boolean} */
  #hovering = false;

  /** @type {boolean} */
  #focused = false;

  /** @type {boolean} */
  #reduced = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.addEventListener('mouseenter', this.#onEnter, { signal });
    this.addEventListener('mouseleave', this.#onLeave, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('visibilitychange', this.#onVisibility, { signal });

    this.#show(0, false);
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  /** Shows the next photo. */
  next = () => {
    this.#show(this.#index + 1);
  };

  /** Shows the previous photo. */
  previous = () => {
    this.#show(this.#index - 1);
  };

  /**
   * Shows the photo at the given index.
   * @param {number} index
   */
  goTo = (index) => {
    this.#show(Number(index));
  };

  /** Pauses or resumes the automatic advance. */
  toggleAutoplay = () => {
    this.#userPaused = !this.#userPaused;
    this.classList.toggle('is-paused', this.#userPaused);

    const button = this.refs.pause;
    if (button) {
      button.setAttribute('aria-pressed', this.#userPaused ? 'true' : 'false');
      button.setAttribute('aria-label', this.#userPaused ? 'Reanudar carrusel' : 'Pausar carrusel');
    }

    if (this.#userPaused) this.#stop();
    else this.#start();
  };

  /**
   * @returns {HTMLElement[]}
   */
  #slides() {
    return [...this.querySelectorAll('[data-pet-hero-slide]')].filter((slide) => slide instanceof HTMLElement);
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #dots() {
    return [...this.querySelectorAll('[data-pet-hero-dot]')].filter((dot) => dot instanceof HTMLButtonElement);
  }

  /**
   * @param {number} index
   * @param {boolean} [restart]
   */
  #show(index, restart = true) {
    const slides = this.#slides();
    const total = slides.length;
    if (!total) return;

    this.#index = (index + total) % total;

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.#dots().forEach((dot, dotIndex) => {
      const active = dotIndex === this.#index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (restart) {
      this.#stop();
      this.#start();
    }
  }

  #start() {
    if (this.#userPaused || this.#hovering || this.#focused || this.#reduced) return;
    if (this.#slides().length < 2) return;

    this.#stop();
    const interval = Number(this.dataset.interval) || 5000;
    this.#timer = window.setInterval(() => this.#show(this.#index + 1, false), interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #onEnter = () => {
    this.#hovering = true;
    this.#stop();
  };

  #onLeave = () => {
    this.#hovering = false;
    this.#start();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#stop();
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#focused = false;
    this.#start();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  };

  #onVisibility = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };
}

if (!customElements.get('pet-hero-slideshow')) {
  customElements.define('pet-hero-slideshow', PetHeroSlideshow);
}
